import React, { useEffect, useMemo, useState } from "react";
import { Box } from "@mui/material";
import { getComplianceControlSections } from "../../../helpers/auth_aws_helper";
import { scorePercent } from "../AuditHub/utils";
import AuditAccountScores from "./AuditAccountScores";
import AuditReportButton from "./AuditReportButton";
import { CardHeader, CardShell, ResourcesTileBody, ScoreTileBody, Tile, tileGridSx, wideTileSx } from "./AuditCardParts";
import { frameworkMatches } from "./ControlAuditPanel";

const sumComponents = (scopes, keys) =>
  scopes.reduce((total, scope) => total + keys.reduce((n, key) => n + (Number(scope.components?.[key]) || 0), 0), 0);

/** Automated results plus manual verdicts - the same counts the audit score is computed from. */
const controlCountsOf = (scopes) => {
  const scored = scopes.filter((scope) => scope.hasScanResult);
  return {
    passed: sumComponents(scored, ["passedControls", "manualPassed"]),
    failed: sumComponents(scored, ["failedControls", "manualFailed"]),
  };
};

/**
 * Audit score for this framework across every account. When the audit covers only this framework,
 * the API's own audit-level score is used as is; otherwise it is pooled the same way the API pools
 * its totals (Σpassed / Σ(passed + failed)) rather than averaging percentages.
 */
const pooledScore = (audit, scopes) => {
  const allScopes = (audit?.accounts || []).flatMap((account) => account.scopes || []);
  if (scopes.length && scopes.length === allScopes.length) return scorePercent(audit?.auditScore);
  const { passed, failed } = controlCountsOf(scopes);
  return passed + failed > 0 ? (passed / (passed + failed)) * 100 : null;
};

const sumSections = (sections) =>
  sections.reduce(
    (acc, data) => {
      acc.total += Number(data.totalResources) || 0;
      acc.passed += Number(data.passedResources) || 0;
      acc.failed += Number(data.failedResources) || 0;
      return acc;
    },
    { total: 0, passed: 0, failed: 0 }
  );

const AuditOverviewHeader = ({ audit, compliance, accountNumber, onAccountChange }) => {
  const accounts = useMemo(() => (Array.isArray(audit?.accounts) ? audit.accounts : []), [audit]);
  const scopes = useMemo(
    () =>
      accounts
        .map((account) => (account.scopes || []).find((item) => frameworkMatches(compliance, item.framework)))
        .filter(Boolean),
    [accounts, compliance]
  );
  const runIds = useMemo(
    () => Array.from(new Set(scopes.map((scope) => scope.latestRunId).filter(Boolean))),
    [scopes]
  );
  const runKey = runIds.join(",");

  const [sections, setSections] = useState({ status: "idle", items: [], failed: 0 });

  useEffect(() => {
    if (!compliance || !runIds.length) {
      setSections({ status: "ready", items: [], failed: 0 });
      return undefined;
    }
    let cancelled = false;
    setSections({ status: "loading", items: [], failed: 0 });
    Promise.allSettled(runIds.map((runId) => getComplianceControlSections({ compliance, runId }))).then((results) => {
      if (cancelled) return;
      const items = results
        .filter((result) => result.status === "fulfilled" && result.value?.status === 200)
        .map((result) => result.value);
      setSections({ status: "ready", items, failed: results.length - items.length });
    });
    return () => {
      cancelled = true;
    };
    // runKey captures the run list; runIds is rebuilt on every audit refresh.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [compliance, runKey]);

  const score = useMemo(() => pooledScore(audit, scopes), [audit, scopes]);
  const controlTotals = useMemo(() => controlCountsOf(scopes), [scopes]);
  const totals = useMemo(() => sumSections(sections.items), [sections.items]);
  const scannedCount = scopes.filter((scope) => scope.latestRunId).length;
  const missingScan = scopes.length > 0 && scannedCount === 0;

  return (
    <CardShell>
      <CardHeader
        eyebrow="Assessment Overview"
        compliance={compliance}
        meta={
          <>
            All {accounts.length} accounts · {scannedCount} of {accounts.length} scanned for this framework
            {sections.failed ? ` · ${sections.failed} unavailable` : ""}
          </>
        }
        action={<AuditReportButton auditId={audit?.auditId} auditCode={audit?.auditCode} status={audit?.status} />}
      />
      <Box sx={tileGridSx}>
        <Tile label="Compliance Score">
          <ScoreTileBody
            score={score == null ? NaN : score}
            passed={controlTotals.passed}
            failed={controlTotals.failed}
            tooltip={score == null ? "Pending Automated Scan Completion" : "Assessment score across all accounts"}
            missing={missingScan}
          />
        </Tile>
        <Tile label="Resources Scanned">
          <ResourcesTileBody
            total={totals.total}
            passed={totals.passed}
            failed={totals.failed}
            loading={sections.status === "loading"}
            missing={missingScan}
          />
        </Tile>
        <AuditAccountScores
          accounts={accounts}
          compliance={compliance}
          accountNumber={accountNumber}
          onAccountChange={onAccountChange}
          sx={wideTileSx}
        />
      </Box>
    </CardShell>
  );
};

export default AuditOverviewHeader;
